import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import api from '../services/api';
import i18n, { applyUserLanguage } from '../i18n';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 检查本地存储的token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // 验证token有效性
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  // clearTokenOnFailure：仅「启动时用本地 token 验活」该在失败时清 token。
  // 登录/注册后的补齐拉取不能清——那会让一次瞬时的 /me 失败把刚登录成功的用户踢下线。
  const fetchUserInfo = async ({ clearTokenOnFailure = true } = {}) => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      applyUserLanguage(response.data.user); // 账号上设过界面语言则以账号为准
      return true;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      if (clearTokenOnFailure) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 登录/注册/手机登录的响应只带基础字段，**不含 trust_level**（后端仅 /auth/me 计算并返回）。
  // 若直接拿它当 user，刚登录的这段时间里 user.trust_level 恒 undefined，前端所有
  // 「T2 手机已验证」判定都会 fail-open 成「已验证」→ T2 主动引导整个失效（要刷新页面
  // 触发启动时的 /me 才恢复）。所以这里登录成功后立即用 /auth/me 补齐为权威用户对象。
  const hydrateUser = async (fallbackUser) => {
    setUser(fallbackUser); // 先乐观置上，避免补齐期间界面空白
    await fetchUserInfo({ clearTokenOnFailure: false });
  };

  const login = async (loginData) => {
    try {
      const response = await api.post('/auth/login', loginData);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await hydrateUser(user);
      
      message.success(i18n.t('common:toast.loginSuccess'));
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || i18n.t('common:toast.loginFailed');
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (registerData) => {
    try {
      const response = await api.post('/auth/register', registerData);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await hydrateUser(user);
      
      message.success(i18n.t('common:toast.registerSuccess'));
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || i18n.t('common:toast.registerFailed');
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 发送手机验证码（开发态返回 devCode 便于联调）
  const sendPhoneCode = async (phone, countryCode) => {
    try {
      const response = await api.post('/auth/phone/send-code', { phone, country_code: countryCode });
      return { success: true, devCode: response.data.devCode };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || i18n.t('common:toast.sendCodeFailed');
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 已登录用户绑定并验证手机号（升级到 T2，可下单/发布需求）。成功后刷新用户信息以更新 trust_level
  const verifyPhone = async ({ phone, code, country_code }) => {
    try {
      await api.post('/auth/phone/verify', { phone, code, country_code });
      await fetchUserInfo();
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || i18n.t('common:toast.phoneVerifyFailed');
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 手机验证码登录（首次自动注册）
  const phoneLogin = async ({ phone, code, country_code }) => {
    try {
      const response = await api.post('/auth/phone/login', { phone, code, country_code });
      const { user, token } = response.data;

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await hydrateUser(user);

      message.success(i18n.t('common:toast.loginSuccess'));
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || i18n.t('common:toast.loginFailed');
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userRole');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    message.success(i18n.t('common:toast.loggedOut'));
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    login,
    register,
    sendPhoneCode,
    phoneLogin,
    verifyPhone,
    logout,
    updateUser,
    fetchUserInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
